import React, { useContext, useEffect, useState } from "react";
import PropTypes from "prop-types";
import { translate } from "react-polyglot";
import { FormSpy } from 'react-final-form';

import { formatProfiles } from "./helpers";

import {
  FormLabel,
  FormSelect,
  FormRadio,
  FormIconChooser,
  BaseForm,
  FormTextArea,
  FormDateInput,
  FormInputHolder
} from "../form";
import { PrimaryButton, SecondaryButton } from "../buttons";
import babyIcons from "../icons/babyIcons";
import ErrorMessage from "../ErrorMessage/ErrorMessage";

import { required } from "../../helpers/formValidators";
import { Error } from "../form/BaseForm";
import { OnChange } from "react-final-form-listeners";
import { FormContext } from "../../context/form-context";

const milestoneList = [
  {
    label: "I opened my eyes for the first time",
    value: "opened-eyes-first-time"
  },
  {
      label: "Today I gained some weight",
      value: "gained-weight"
  },
  {
      label: "First cuddle",
      value: "first-cuddle"
  },
  {
      label: "Wire free!",
      value: "wire-free"
  },
  {
      label: "I smiled for the first time",
      value: "smiled-first-time"
  },
  {
      label: "First Selfie",
      value: "first-selfie"
  },
  {
      label: "I latched today",
      value: "latched-today"
  },
  {
      label: "Skin-to-skin superstar",
      value: "skin-superstar"
  },
  {
      label: "First bath",
      value: "first-bath"
  },
  {
      label: "First diaper changed",
      value: "first-diaper-changed"
  },
  {
      label: "First time in cot or crib",
      value: "first-cot-crib"
  },
  {
      label: "First time in skin to skin contact",
      value: "skin-skin-contact"
  },
  {
      label: "First holding baby clothed",
      value: "holding-baby-clothing"
  },
  {
      label: "First feed",
      value: "first-feed"
  },
  {
      label: "First breastfeed",
      value: "first-breastfeed"
  },
  {
      label: "First song",
      value: "first-song"
  },
  {
      label: "First book read",
      value: "first-book-read"
  },
];

export { milestoneList };

const MilestoneForm = (props) => {
  const {
    babies,
    profiles,
    selectedProfile,
    showFirstTimeSavingModal,
    showUnSavedModal,
    showSaveEntryModal,
    milestoneRecordsPost,
    milestoneRecordsPatch,
    hideTrackerModals,
    handleRecords,
    accountId,
    t
  } = props;

  const defaultValues = {
    babyId: null,
    providerId: selectedProfile.id,
    date: new Date()
  }

  const [initialValues, setInitialValues] = useState(undefined);

  const formContext = useContext(FormContext);

  const toggleWarningModal = () => {
    return formContext.isDirty ? showUnSavedModal() : hideTrackerModals();
  };

  useEffect(() => {
    // if there's form data in the context, use that instead of using default values
    if (formContext && Object.keys(formContext.formValues).length > 0) {
      setInitialValues(formContext.formValues);
      return
    }

    const latestRecord = handleRecords('milestones', null) || {};

    setInitialValues({
      ...defaultValues,
      ...latestRecord
    });
  }, [])

  const onSubmit = (values) => {
    const recordBody = {
      ...values,
      providerId: values.providerId === "other" ? null : values.providerId,
      otherProvider: values.providerId !== "other" ? null : values.otherProvider
    };

    if (values.id) {
      // Update existing record
      return milestoneRecordsPatch(
        { accountId: accountId, babyId: values.babyId, id: values.id },
        { body: JSON.stringify(recordBody) }
      ).then(data => {
        if (data) {
          hideTrackerModals();
        }
      }).catch(error => <ErrorMessage />);
    } else {
      // Create new record
      return milestoneRecordsPost(
        { accountId: accountId, babyId: babies.length === 1 ? babies[0].id : values.babyId },
        { body: JSON.stringify(recordBody) }
      ).then(data => {
        if (data) {
          hideTrackerModals();
        }
      }).catch(error => <ErrorMessage />);
    }
  };

  const onSave = (values) => {
    if (!showFirstTimeSavingModal) {
      onSubmit(values);
    } else {
      formContext.setValues(values);
      formContext.setFunctions({
        onSubmit: onSubmit,
      });

      return showSaveEntryModal();
    }
  }

  const babyIds = babies.map(baby => baby.id);

  if (!initialValues) {
    return <div></div>
  }

  return (
    <BaseForm
      className="milestoneModal"
      onSubmit={onSave}
      initialValues={initialValues}
    >
      <h2 className="title">{t("tracker.trackMilestone")}</h2>

      {babies && babies.length > 1 && (
        <React.Fragment>
          <FormIconChooser>
            {babies.map((baby, index) => (
              <FormRadio
                key={baby.id}
                value={baby.id}
                name="babyId"
                imageSrc={babyIcons[baby.icon]}
                validate={required}
              >
                {baby.name}
              </FormRadio>
            ))}
          </FormIconChooser>

          <OnChange name="babyId">
            {(value, previous) => {
              if (value === previous) {
                return;
              }

              const latestRecord =  handleRecords('milestones', value) || {};

              setInitialValues({
                ...defaultValues,
                ...latestRecord,
                babyId: value
              });
            }}
          </OnChange>

          <Error name="babyId" />
        </React.Fragment>
      )}

      <FormLabel for="providerId">Provider</FormLabel>
      <FormSelect
        name="providerId"
        options={formatProfiles(profiles)}
        validate={required}
        includeOther
        otherName="otherProvider"
      />

      <div>
        <FormLabel>{t("tracker.chooseMilestone")}</FormLabel>
        <FormSelect
          placeholder={"Select"}
          name="milestoneType"
          options={milestoneList}
          includeOther
          validate={required}
          otherName="otherMilestone"
        />
      </div>

      <div>
        <FormTextArea
          maxLength={300}
          name="note"
          labelRenderer={() => <FormLabel note="Optional">Note</FormLabel>}
        />
      </div>

      <div>
        <FormLabel>{t("tracker.date")}</FormLabel>
        <FormDateInput
          name="date"
          validate={required}
          dateFormat='MMM, dd yyyy'
        />
      </div>

      <FormSpy subscription={{ submitting: true }}>
        {({ submitting }) => {
          return (
            <FormInputHolder>
              <SecondaryButton
                type="button"
                label="Cancel"
                onClick={toggleWarningModal}
              />

              <PrimaryButton type="submit" label="Save" disabled={submitting} />
            </FormInputHolder>
          );
        }}
      </FormSpy>

      <FormSpy
        subscription={{ dirty: true, dirtyFields: true, visited: true, values: true }}
        onChange={({ dirty, visited, values }) => {
          if (dirty) {
            let actuallyDirty = false;

            formContext.setValues(values);

            Object.keys(visited).forEach((key) => {
              if (visited[key]) {
                actuallyDirty = true;
              }
            });

            actuallyDirty ? formContext.setDirty() : formContext.notDirty();
          }
        }}
      />
    </BaseForm>
  );
}

MilestoneForm.defaultProps = {
  error: null,
  profiles: [],
  selectedProviderId: null,
  handleSubmit: () => {},
  t: () => {}
};

MilestoneForm.propTypes = {
  error: PropTypes.string,
  submitting: PropTypes.bool,
  babies: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string,
      name: PropTypes.string
    })
  ).isRequired,
  profiles: PropTypes.arrayOf(PropTypes.object),
  selectedProviderId: PropTypes.string,
  handleSubmit: PropTypes.func,
  showFirstTimeSavingModal: PropTypes.bool,
  showSaveEntryModal: PropTypes.func,
  t: PropTypes.func
};

export default translate()(MilestoneForm);



// WEBPACK FOOTER //
// ./src/components/ActivityTracker/MilestoneForm.js